import React, { useMemo, useRef, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

/**
 * MemoryGarden — a soft, living visualization of what Rammy remembers.
 *
 * Each stored memory blooms as a colored dot on a dark canvas. Positions are
 * deterministic per-memory-id (so dots don't jump around on re-render), colors
 * come from the memory's category, and each dot breathes on a slight per-dot
 * delay to feel alive. Hover reveals the full memory. Newly-added memories
 * enter with a bloom animation and Rammy pulses in the corner.
 *
 * Empty state is intentionally warm: "Your garden grows as we chat."
 */

const CATEGORY_HUE = {
  Personal: 190,          // cyan
  Family: 340,            // rose
  Work: 32,               // amber
  Goals: 265,             // violet
  Preferences: 145,       // emerald
  Health: 8,              // coral
  Entertainment: 210,     // sky
  "Important Dates": 40,  // gold
  Conversations: 285,     // orchid
  "Follow-ups": 100,      // lime
};

function hue(cat) { return CATEGORY_HUE[cat] ?? 190; }
function fill(cat, alpha = 1) { return `hsla(${hue(cat)}, 85%, 62%, ${alpha})`; }
function glow(cat) { return `hsla(${hue(cat)}, 90%, 70%, 0.85)`; }

// Cheap deterministic hash → number
function hash(id = "") {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function MemoryGarden({ items = [], onSelect }) {
  const [containerSize, setContainerSize] = useState({ w: 800, h: 260 });
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute non-overlapping-ish positions using golden-angle spiral seeded by hash.
  const dots = useMemo(() => {
    const n = items.length;
    if (n === 0) return [];
    const cx = containerSize.w / 2;
    const cy = containerSize.h / 2;
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const maxR = Math.min(containerSize.w, containerSize.h * 1.6) * 0.42;
    return items.map((m, i) => {
      const h = hash(m.id || String(i));
      // Distribute along a phyllotaxis spiral but jitter by hash for variety.
      const t = i + (h % 7) / 10;
      const r = Math.sqrt(t / n) * maxR;
      const theta = t * goldenAngle + (h % 100) / 100;
      const x = cx + Math.cos(theta) * r;
      const y = cy + Math.sin(theta) * r * 0.75; // squish vertically
      const size = 8 + (h % 8);
      const delay = ((h % 100) / 100) * 3.5;
      return { x, y, size, delay, m };
    });
  }, [items, containerSize.w, containerSize.h]);

  const empty = items.length === 0;

  return (
    <div
      ref={ref}
      data-testid="memory-garden"
      className="relative rounded-3xl overflow-hidden border border-[var(--rammy-border)] rammy-shadow"
      style={{
        background: "radial-gradient(120% 100% at 20% 10%, #1a2130 0%, #0d1219 55%, #080c12 100%)",
        minHeight: 260,
      }}
    >
      {/* Header */}
      <div className="relative z-10 p-6 pointer-events-none">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--rammy-cyan)] font-semibold flex items-center gap-2">
          <Sparkles size={12} /> Memory garden
        </div>
        <div className="text-white/95 font-display text-xl md:text-2xl mt-1.5 max-w-md leading-snug">
          {empty
            ? "Your garden grows as we chat."
            : `${items.length} ${items.length === 1 ? "thing" : "things"} I remember about you.`}
        </div>
        {!empty && (
          <div className="text-white/45 text-xs mt-1">
            Hover a dot to peek. Colors are categories. New memories bloom on their own.
          </div>
        )}
      </div>

      {/* Dots */}
      {dots.map(({ x, y, size, delay, m }) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onSelect?.(m)}
          data-testid={`garden-dot-${m.id}`}
          data-category={m.category}
          className="absolute group focus:outline-none"
          style={{
            left: x - size / 2,
            top: y - size / 2,
            width: size,
            height: size,
          }}
          aria-label={m.title}
        >
          <span
            className="block rounded-full transition-transform duration-200 group-hover:scale-[2.4]"
            style={{
              width: size,
              height: size,
              background: `radial-gradient(circle at 30% 30%, ${fill(m.category, 1)}, ${fill(m.category, 0.4)})`,
              boxShadow: m.has_image
                ? `0 0 ${size * 1.4}px ${glow(m.category)}, 0 0 0 1.5px rgba(255, 200, 100, 0.9)`
                : `0 0 ${size * 1.4}px ${glow(m.category)}`,
              animation: `garden-breathe 4.5s ease-in-out ${delay}s infinite`,
            }}
          />
          {/* Tooltip */}
          <div
            className="pointer-events-none opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-max max-w-[260px] rounded-xl bg-[var(--rm-surface)] p-3 text-left text-[var(--rammy-text)] shadow-2xl border border-[var(--rammy-border)] z-20"
          >
            {m.has_image && m.image_thumb && (
              <img
                src={`data:${m.image_mime || "image/jpeg"};base64,${m.image_thumb}`}
                alt=""
                className="w-full max-w-[220px] rounded-lg object-cover mb-2 max-h-28"
              />
            )}
            <div className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: `hsl(${hue(m.category)}, 55%, 42%)` }}>
              {m.category}
            </div>
            <div className="font-semibold mt-0.5 text-sm">{m.title}</div>
            {m.detail && <div className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed">{m.detail}</div>}
          </div>
        </button>
      ))}

      {/* Empty-state gentle hint */}
      {empty && (
        <div className="relative z-10 px-6 pb-6 text-white/60 text-sm max-w-md">
          Tell me one thing about yourself in Chat — a name, a city, a favorite. I&rsquo;ll plant it here.
        </div>
      )}

      {/* Local keyframes (inline so this component is self-contained) */}
      <style>{`
        @keyframes garden-breathe {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50%      { transform: scale(1.18); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
